/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';

import { Constants, Partner, CoreField, SiftrockField } from '../constants';
import { PartnerField, PartnerSiftrockFieldMap } from '../models';
import { PartnerService } from '../../services/partner.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'partner-siftrockfield-picker',
  templateUrl: './partner-siftrockfield-picker.component.html'
})
export class PartnerSiftrockfieldPickerComponent implements OnInit {

  @Input()
  partner: Partner;

  @Input()
  skillType: string;

  @Input()
  fieldMap: Array<PartnerSiftrockFieldMap>;

  @Input()
  partnerFields: Array<PartnerField>;

  @Input()
  usedFields: Array<PartnerField>;

  @Output('onChange')
  onChange: EventEmitter<Array<PartnerSiftrockFieldMap>> = new EventEmitter<Array<PartnerSiftrockFieldMap>>();

  @Output('onRefreshFields')
  onRefreshFields: EventEmitter<any> = new EventEmitter<any>();

  // siftrockfield drop down component
  ddSiftrockFields: any[] = [];
  siftrockFieldDDSettings: any = {
    singleSelection: true,
    text: 'Select field',
    enableSearchFilter: true,
    badgeShowLimit: 1,
    maxHeight: 260
  };
  ddSiftrockField: any;

  // partner field drop down component
  ddPartnerFields: any[] = [];
  partnerFieldDDSettings: any = {
    singleSelection: true,
    text: 'Select field',
    enableSearchFilter: true,
    badgeShowLimit: 1,
    maxHeight: 260
  };
  ddPartnerField: any;

  coreFields: CoreField[] = [];
  fields: Array<PartnerField>;
  addingField: boolean = false;
  currentPartnerField: PartnerField;
  currentSiftrockField: SiftrockField;
  errorState: boolean = false;

  constructor(private constants: Constants,
    private modalService: BsModalService,
    private partnerService: PartnerService) { }

  ngOnInit() {
    // if there are no fields mapped yet, initialize the array
    if (!this.fieldMap) {
      this.fieldMap = [];
    }

    if (this.partner) {
      this.loadFields();
    }
  }

  /**
   * Listen for changes to the partner value in order to properly refresh fields and re-set state
   * when switching from one partner to another
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.partner && changes.partner.previousValue && changes.partner.previousValue.name) {
      // Refresh state of field map controls when partner changes
      this.addingField = false
      this.currentSiftrockField = null;
      this.currentPartnerField = null;
      this.errorState = false;
      this.loadFields();
    }
    else if (changes.usedFields) {
      // change triggers if usedFields changes across all field map controls - ensure fields can
      // only be selected once
      this.loadFields();
    }
  }

  formValid() {
    if (this.currentPartnerField && this.currentSiftrockField) {
      return true;
    }
    return false;
  }

  selectPartnerField(field: any) {
    this.currentPartnerField = this.fields.find(f => { return f.id == field.id });
  }

  selectSiftrockField(field: any) {
    this.currentSiftrockField = this.constants.siftrockFields.find(f => { return f.fieldName == field.id });
  }

  addField() {
    this.canAddField()
      .then(canAdd => {
        if (canAdd) {
          this.fieldMap.push({
            partnerField: this.currentPartnerField,
            siftrockField: this.currentSiftrockField
          });

          this.onChange.emit(this.fieldMap);
          this.loadFields();  // refresh from cache to set usedFields up correctly
          this.addingField = false;
          this.currentSiftrockField = null;
        }
      });

  }

  cancelAddField() {
    this.addingField = false;
    this.currentPartnerField = null;
    this.currentSiftrockField = null;
    this.ddPartnerField = [];
  }

  remove(field: PartnerSiftrockFieldMap) {
    this.fieldMap.splice(this.fieldMap.findIndex(f => { return f.partnerField.id == field.partnerField.id }), 1);
    this.loadFields(); // refresh from cache to set usedFields up correctly
    this.onChange.emit(this.fieldMap);
  }

  refreshFields() {
    this.onRefreshFields.emit(null);
  }

  private loadFields() {
    // for the Create skill type, set up core fields in the UI so users know they are automatically mapped
    if (this.skillType == 'create') {
      this.coreFields = [];
      this.constants.coreFields.forEach(cf => {
        if (cf.partner == this.partner.name) {
          this.coreFields.push(cf);
        }
      });
    }


    this.errorState = false;
    this.fields = [];
    this.ddPartnerField = [];
    this.ddPartnerFields = [];

    this.ddSiftrockField = [];
    this.ddSiftrockFields = [];
    this.constants.siftrockFields.forEach(sf => {
      if (sf.trackTask && !sf.isCore) {
        this.ddSiftrockFields.push({
          id: sf.fieldName,
          itemName: sf.display
        });
      }
    });

    this.partnerFields.forEach(f => {
      // only make the field available if it hasn't been used
      if (this.usedFields.findIndex(uf => { return uf.id == f.id}) < 0 &&
          this.fieldMap.findIndex(uf => { return uf.partnerField.id == f.id}) < 0) {
        this.fields.push({
          id: f.id,
          name: f.name,
          display: f.display,
          type: f.type ? f.type : 'n/a'
        });

        this.ddPartnerFields.push({
          id: f.id,
          itemName: f.display
        });
      }
    });

    this.fields.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.ddPartnerFields.sort((a, b) => {
      if (a.itemName < b.itemName) {
        return -1;
      }
      else {
        return 1;
      }
    });
  }

  private canAddField(): Promise<any> {
    if (this.currentPartnerField.type != this.currentSiftrockField.type) {
      return new Promise((resolve, reject) => {
        this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
          initialState: {
            message: `WARNING: you are adding a field mapping that may cause this task to fail due to a mismatch in data types. Please confirm this is what you'd like to do.`,
            yesBtn: 'Confirm',
            confirm: function () {
              // confirmed
              resolve(true);
            }
          }
        }));
      });
    }
    else {
      return Promise.resolve(true);
    }
  }
}



// WEBPACK FOOTER //
// ./client/app/global/partner-siftrockfield-picker/partner-siftrockfield-picker.component.ts